import * as React from "react";
import { Button, Dropdown, notification } from "antd";
import { clearItem, gvcScopedKinds, toSortedJSON, yamlDumpAll } from "../../../services/utils";
import { convertAllLinksToRelative, parseLink } from "../../../utils/linkParser/linkParser";
import { linksOf, request } from "../../../services/cpln";
import { workloadHelpers } from "../../../mst/kinds/workload.helpers";
import { confirmSecretReveal } from "../../../utils/confirmSecretReveal";
import { TerraformImportModal } from "../../../components/modals/tfImportModal";
import { itemHelpers as itemHelpersBase } from "../../../mst/kinds/item.helpers";
import { K8sCrdExportModal } from "../../../components/modals/k8sCrdExportModal";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { Kind } from "../../../schema/types/base";
import { DownOutlined } from "@ant-design/icons";

interface IProps {
  kind: Kind;
  selfLinks: string[];
}
export const TableExportButton = ({ kind, selfLinks }: IProps) => {
  const context = useAppSelector(selectContext);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [isLoading, setIsLoading] = React.useState(false);
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [isK8sCrdExportModalOpen, setIsK8sCrdExportModalOpen] = React.useState<boolean>(false);

  const canExportToTerraform = terraformSupportedKinds.includes(kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind);
  const itemHelpers = itemHelpersBase({ org: context.org, gvc: context.gvc, kind: kind });

  async function handleExportMenuClick(e: any) {
    setIsLoading(true);
    switch (e.key) {
      case "json":
        await exportJSON(false);
        break;
      case "yaml":
        await exportYaml(false);
        break;
      case "json-slim":
        await exportJSON(true);
        break;
      case "yaml-slim":
        await exportYaml(true);
        break;
      case "terraform":
        await exportTerraform();
        break;
      case "k8s-crd":
        await exportK8sCrd();
        break;
    }
    setIsLoading(false);
  }

  function getFileName(): string {
    let fileName: string = context.org as string;

    // Only include GVC in filename if all items are from the same GVC
    if (gvcScopedKinds.includes(kind) && selfLinks.length > 0) {
      const gvcSet = new Set(selfLinks.map(link => parseLink(link, { useInputCtx: true }).gvc).filter(Boolean));
      if (gvcSet.size === 1) {
        fileName += `_${Array.from(gvcSet)[0]}`;
      }
    }

    fileName += `_${kind}s`;

    return fileName;
  }

  function autoDownloadWithElementA(blobData: any, extension: "json" | "yaml" | "tf", filename?: string) {
    const file = new Blob([blobData], { type: `text/${extension}` });
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.style.display = "none";
    a.classList.add("cpln-temp-a");
    a.download = (filename || getFileName()) + "." + extension;
    a.href = href;
    a.click();
  }

  async function exportItems(slim: boolean) {
    try {
      let _items: any[] = [];
      for (let selfLink of selfLinks) {
        const { data }: any = await request({ url: selfLink });
        _items = _items.concat(data);
      }

      if (kind === "secret") {
        const shouldReveal = await confirmSecretReveal({ okText: "Reveal & Export", cancelText: "Export Without Revealing" });
        if (shouldReveal) {
          const revealPromises: Promise<any>[] = [];
          for (let _item of _items) {
            revealPromises.push(request({ url: linksOf(_item).reveal! }));
          }
          const revealedItems: any[] = await Promise.allSettled(revealPromises);

          for (let revealedRes of revealedItems) {
            if (revealedRes.status !== "fulfilled") {
              continue;
            }
            const revealed = revealedRes.value.data;
            const index = _items.findIndex((i) => i.name === revealed.name);
            if (index >= 0) {
              _items[index] = revealed;
            }
          }
        }
      }

      if (kind === "workload") {
        const deploymentPromises: Promise<any>[] = [];
        for (let _item of _items) {
          deploymentPromises.push(request({ url: linksOf(_item).deployment! }));
        }
        const deploymentResponses: any[] = await Promise.allSettled(deploymentPromises);

        for (let deploymentRes of deploymentResponses) {
          const index = _items.findIndex((i) => linksOf(i).self === linksOf(deploymentRes.value.data).workload);
          if (index >= 0) {
            if (deploymentRes.status !== "fulfilled") {
              _items[index].status.ready = "unknown";
              _items[index].status.readyLatest = "unknown";
              continue;
            }

            const health = workloadHelpers.getHealth(deploymentRes.value.data.items, _items[index]);
            _items[index].status.ready = health.isReady;
            _items[index].status.readyLatest = health.isLatestReady;
            _items[index].status.readyCheckTimestamp = new Date().toISOString();
          }
        }
      }

      for (let _itemIndex in _items) {
        let _item = _items[_itemIndex];
        // Add gvc property for GVC-scoped kinds (must be done before clearItem which removes links)
        if (gvcScopedKinds.includes(_item.kind)) {
          const selfLink = linksOf(_item).self;
          if (selfLink) {
            _item.gvc = parseLink(selfLink, { useInputCtx: true }).gvc || "";
          }
        }
        if (slim) {
          clearItem(_item);
        }
        _item = convertAllLinksToRelative(_item);
        _item = toSortedJSON(_item);
        _items[_itemIndex] = _item;
      }

      return _items;
    } catch (e) {
      return false;
    }
  }

  async function exportJSON(slim: boolean) {
    try {
      const data = await exportItems(slim);
      const blob = JSON.stringify(data, null, 2);
      autoDownloadWithElementA(blob, "json");
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function exportYaml(slim: boolean) {
    try {
      const data = await exportItems(slim);
      const blob = yamlDumpAll(data, { indent: 2, noRefs: true });
      autoDownloadWithElementA(blob, "yaml");
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function exportTerraform() {
    setTerraformImportCommand(await itemHelpers.handleExportTerraform([]));
  }

  async function exportK8sCrd() {
    setIsK8sCrdExportModalOpen(true);
  }

  const exportMenuItems = [
    { key: "json", label: "JSON" },
    { key: "yaml", label: "YAML" },
    { key: "json-slim", label: "JSON Slim" },
    { key: "yaml-slim", label: "YAML Slim" },
  ];

  if (canExportToTerraform) {
    exportMenuItems.push({ key: "terraform", label: "Terraform" });
  }

  if (canExportToK8sCrd) {
    exportMenuItems.push({ key: "k8s-crd", label: "K8s CRD" });
  }

  return (
    <>
      <Dropdown menu={{ onClick: handleExportMenuClick, items: exportMenuItems }} placement={"top"} trigger={["click"]}>
        <Button loading={isLoading} type="default" icon={<DownOutlined />}>
          Export
        </Button>
      </Dropdown>
      {terraformImportCommand ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setTerraformImportCommand("")} />
      ) : null}
      {isK8sCrdExportModalOpen ? (
        <K8sCrdExportModal
          kind={kind}
          links={[]}
          onClose={() => {
            setIsK8sCrdExportModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
